// PortalSwitcher Component - Portal Selection and Switching
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Menu,
  MenuItem,
  Button,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  Box,
  Avatar,
  Chip,
  alpha,
  useTheme,
} from '@mui/material';
import {
  SwapHoriz as SwapHorizIcon,
  Dashboard as DashboardIcon,
  AdminPanelSettings as AdminIcon,
  Business as BusinessIcon,
  SupervisorAccount as SuperAdminIcon,
  Security as BureauIcon,
  KeyboardArrowDown as ArrowDownIcon,
} from '@mui/icons-material';
import { useAuth, usePermissions } from '../../hooks';
import { getDefaultRedirectForUser } from '../../../routing';

/**
 * Portal Configuration
 */
const PORTAL_CONFIG = {
  bureau: {
    name: 'Bureau Portal',
    description: 'Operations & Monitoring',
    icon: BureauIcon,
    color: '#ff9800',
    path: '/bureau',
    requiredRoles: ['Bureau Agents Staff', 'Admin', 'Super Admin'],
  },
  admin: {
    name: 'Admin Portal',
    description: 'System Administration',
    icon: AdminIcon,
    color: '#f44336',
    path: '/admin',
    requiredRoles: ['Admin', 'Super Admin'],
  },
  client: {
    name: 'Client Portal',
    description: 'Fleet Management',
    icon: BusinessIcon,
    color: '#2196f3',
    path: '/client',
    requiredRoles: ['Client', 'Admin', 'Super Admin'],
  },
  'super-admin': {
    name: 'Super Admin Portal',
    description: 'RBAC & System Oversight',
    icon: SuperAdminIcon,
    color: '#9c27b0',
    path: '/super-admin',
    requiredRoles: ['Super Admin'],
  },
};

/**
 * Get current portal from pathname
 */
const getCurrentPortal = (pathname) => {
  const segments = pathname.split('/').filter(Boolean);
  const firstSegment = segments[0];

  if (firstSegment && PORTAL_CONFIG[firstSegment]) {
    return firstSegment;
  }

  return null;
};

/**
 * Portal Menu Item Component
 */
const PortalMenuItem = ({ portal, config, isActive, onClick, disabled = false }) => {
  const theme = useTheme();
  const IconComponent = config.icon;

  return (
    <MenuItem
      onClick={() => onClick(portal)}
      disabled={disabled}
      sx={{
        py: 1.5,
        px: 2,
        borderRadius: 1,
        mx: 0.5,
        my: 0.25,
        '&:hover': {
          backgroundColor: alpha(config.color, 0.08),
        },
        ...(isActive && {
          backgroundColor: alpha(config.color, 0.12),
          borderLeft: `4px solid ${config.color}`,
        }),
      }}
    >
      <ListItemIcon sx={{ minWidth: 40 }}>
        <Avatar
          sx={{
            width: 32,
            height: 32,
            backgroundColor: alpha(config.color, 0.1),
            color: config.color,
          }}
        >
          <IconComponent fontSize="small" />
        </Avatar>
      </ListItemIcon>

      <ListItemText
        primary={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body1" fontWeight={500}>
              {config.name}
            </Typography>
            {isActive && (
              <Chip
                label="Current"
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.7rem',
                  backgroundColor: config.color,
                  color: 'white',
                }}
              />
            )}
          </Box>
        }
        secondary={config.description}
        secondaryTypographyProps={{
          fontSize: '0.75rem',
          color: 'text.secondary',
        }}
      />
    </MenuItem>
  );
};

/**
 * Portal Switcher Component
 */
export const PortalSwitcher = ({
  variant = 'button', // button, menu, dropdown
  showCurrentPortal = true,
  size = 'medium',
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { canAccessPortal } = usePermissions();

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  // Get current portal
  const currentPortal = getCurrentPortal(location.pathname);
  const currentConfig = currentPortal ? PORTAL_CONFIG[currentPortal] : null;

  // Get available portals based on user permissions
  const availablePortals = Object.entries(PORTAL_CONFIG).filter(([portal, config]) =>
    canAccessPortal(portal)
  );

  // Handle menu open/close
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // Handle portal switch
  const handlePortalSwitch = (portal) => {
    const config = PORTAL_CONFIG[portal];
    if (config && portal !== currentPortal) {
      navigate(config.path);
    }
    handleClose();
  };

  // Handle quick switch to user's default portal
  const handleQuickSwitch = () => {
    const defaultRedirect = getDefaultRedirectForUser(user);
    if (defaultRedirect !== location.pathname) {
      navigate(defaultRedirect);
    }
  };

  // Don't render if user has access to only one portal
  if (availablePortals.length <= 1) {
    return null;
  }

  // Render button variant
  if (variant === 'button') {
    return (
      <>
        <Button
          variant="outlined"
          startIcon={<SwapHorizIcon />}
          endIcon={<ArrowDownIcon />}
          onClick={handleClick}
          size={size}
          sx={{
            borderColor: currentConfig?.color || theme.palette.divider,
            color: currentConfig?.color || theme.palette.text.primary,
            '&:hover': {
              borderColor: currentConfig?.color || theme.palette.primary.main,
              backgroundColor: alpha(currentConfig?.color || theme.palette.primary.main, 0.04),
            },
          }}
        >
          {showCurrentPortal && currentConfig
            ? `Switch from ${currentConfig.name}`
            : 'Switch Portal'}
        </Button>

        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          PaperProps={{
            sx: {
              minWidth: 280,
              maxWidth: 320,
              mt: 1,
              border: `1px solid ${theme.palette.divider}`,
            },
          }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Switch Portal
            </Typography>
          </Box>

          <Divider />

          {availablePortals.map(([portal, config]) => (
            <PortalMenuItem
              key={portal}
              portal={portal}
              config={config}
              isActive={portal === currentPortal}
              onClick={handlePortalSwitch}
            />
          ))}

          <Divider sx={{ my: 1 }} />

          <MenuItem onClick={handleQuickSwitch} sx={{ py: 1, px: 2 }}>
            <ListItemIcon>
              <DashboardIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Go to Default Portal" secondary="Based on your primary role" />
          </MenuItem>
        </Menu>
      </>
    );
  }

  // Render compact variant for mobile/sidebar
  if (variant === 'compact') {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 1,
          borderRadius: 1,
          backgroundColor: alpha(theme.palette.background.paper, 0.5),
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        {currentConfig && (
          <Avatar
            sx={{
              width: 24,
              height: 24,
              backgroundColor: currentConfig.color,
              color: 'white',
            }}
          >
            <currentConfig.icon fontSize="small" />
          </Avatar>
        )}

        <Button
          size="small"
          onClick={handleClick}
          endIcon={<ArrowDownIcon fontSize="small" />}
          sx={{ minWidth: 'auto', fontSize: '0.75rem' }}
        >
          {currentConfig ? currentConfig.name : 'Switch Portal'}
        </Button>

        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          PaperProps={{
            sx: { minWidth: 240, mt: 1 },
          }}
        >
          {availablePortals.map(([portal, config]) => (
            <PortalMenuItem
              key={portal}
              portal={portal}
              config={config}
              isActive={portal === currentPortal}
              onClick={handlePortalSwitch}
            />
          ))}
        </Menu>
      </Box>
    );
  }

  return null;
};

/**
 * Portal indicator badge for showing current portal
 */
export const PortalIndicator = ({ portal, size = 'medium' }) => {
  const config = PORTAL_CONFIG[portal];

  if (!config) return null;

  const IconComponent = config.icon;

  return (
    <Chip
      icon={<IconComponent fontSize="small" />}
      label={config.name}
      size={size}
      sx={{
        backgroundColor: alpha(config.color, 0.1),
        color: config.color,
        border: `1px solid ${alpha(config.color, 0.2)}`,
        fontWeight: 500,
      }}
    />
  );
};

export default PortalSwitcher;
